import { useLayoutEffect, useRef, useState } from 'react';
import { Avatar } from './Avatar';
import { Button, TextLink } from './Button';

/**
 * Comment Thread.dc.html, plus a Delete action next to Edit (decided
 * 2026-09-09: own comments can be deleted; no mentions).
 *
 * One level of replies. Cmd/Ctrl+Enter posts; Esc cancels an edit or reply.
 * Guests see "Sign in to join the discussion" when `onSignin` is given.
 *
 * Two layouts. `composer: 'top'` (the default) is the page thread: the
 * composer first, the comments under it, the page scrolls. `'bottom'` is the
 * viewer's dock (decided 2026-10-04): the comments scroll in their own box,
 * oldest first, and the composer stays pinned under them like any chat, so
 * it is always within reach however short the dock is. The box fills what it
 * is given, so the parent sets the height.
 */
export interface ThreadComment {
  id: string;
  name: string;
  isYou?: boolean;
  text: string;
  /** relative time, already formatted ("2 d ago") */
  when: string;
  edited?: boolean;
  replies?: ThreadComment[];
}

export interface CommentThreadProps {
  comments: ThreadComment[];
  me: { name: string };
  canPost: boolean;
  placeholder?: string;
  emptyText?: string;
  onPost?: (text: string) => void;
  onReply?: (commentId: string, text: string) => void;
  onEdit?: (id: string, text: string) => void;
  onDelete?: (id: string) => void;
  onSignin?: () => void;
  composer?: 'top' | 'bottom';
}

const submit = (e: React.KeyboardEvent) => e.key === 'Enter' && (e.metaKey || e.ctrlKey);

/** How many a thread holds, replies included: the dock's header, the rail's badge, the scroll trigger. */
export function threadSize<T extends { replies?: T[] }>(rows: readonly T[] | undefined): number {
  return (rows ?? []).reduce((n, c) => n + 1 + (c.replies?.length ?? 0), 0);
}

/** How long after your own post the box still follows the thread to its newest comment. */
const FOLLOW_OWN_POST_MS = 10_000;

const textareaStyle = (small?: boolean, editing?: boolean): React.CSSProperties => ({
  width: '100%',
  boxSizing: 'border-box',
  padding: '8px 10px',
  fontSize: small ? 13 : 13.5,
  lineHeight: 1.5,
  fontFamily: 'inherit',
  color: 'var(--tr-ink)',
  border: `1px solid ${editing ? 'var(--tr-dash)' : 'var(--tr-line-2)'}`,
  borderRadius: 8,
  background: 'var(--tr-surface)',
  outline: 'none',
  resize: 'vertical',
});

function SmallActions({ primary, onPrimary, onCancel }: { primary: string; onPrimary: () => void; onCancel: () => void }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      <Button variant="outlined" size={28} px={10} fontSize={12.5} onClick={onPrimary}>
        {primary}
      </Button>
      <Button variant="ghost" size={28} px={10} fontSize={12.5} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

export function CommentThread({ comments, me, canPost, placeholder = 'Add a comment', emptyText = 'No comments yet.', onPost, onReply, onEdit, onDelete, onSignin, composer = 'top' }: CommentThreadProps) {
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  // The dock's scrolling box: opened at its newest comment once there are
  // comments to show (they arrive after the first render), and brought back
  // there when this person's own comment or reply lands. Somebody else's
  // comment never moves what the reader is reading; an own post that failed
  // leaves the box following for a few seconds at most.
  const listRef = useRef<HTMLDivElement>(null);
  const followUntil = useRef(Number.POSITIVE_INFINITY);
  const count = threadSize(comments);
  useLayoutEffect(() => {
    if (composer !== 'bottom' || count === 0 || Date.now() > followUntil.current) return;
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    followUntil.current = 0;
  }, [composer, count]);

  const post = () => {
    const t = draft.trim();
    if (!t) return;
    onPost?.(t);
    setDraft('');
    followUntil.current = Date.now() + FOLLOW_OWN_POST_MS;
  };
  const sendReply = (id: string) => {
    const t = replyDraft.trim();
    if (!t) return;
    onReply?.(id, t);
    setReplyTo(null);
    setReplyDraft('');
    followUntil.current = Date.now() + FOLLOW_OWN_POST_MS;
  };
  const saveEdit = (id: string) => {
    const t = editDraft.trim();
    if (!t) return;
    onEdit?.(id, t);
    setEditId(null);
    setEditDraft('');
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditDraft('');
  };

  const row = (c: ThreadComment, reply: boolean) => {
    const editing = editId === c.id;
    const canEdit = canPost && !!c.isYou && !editing;
    return (
      <div key={c.id} style={{ display: 'flex', gap: reply ? 8 : 10, alignItems: 'flex-start' }}>
        <Avatar name={c.name} size={reply ? 22 : 28} style={reply ? { fontSize: 8 } : undefined} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: reply ? 3 : 4, flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, fontSize: reply ? 12 : 12.5, color: 'var(--tr-hint)', flexWrap: 'wrap' }}>
            <span style={{ fontSize: reply ? 12.5 : 13, fontWeight: 500, color: 'var(--tr-ink)' }}>{c.name}</span>
            <span>{c.when}</span>
            {c.edited ? <span title={reply ? 'This reply was edited' : 'This comment was edited'}>edited</span> : null}
          </div>
          {!editing ? (
            <p style={{ margin: 0, fontSize: reply ? 13 : 13.5, lineHeight: 1.5, color: 'var(--tr-ink)', whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{c.text}</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea
                value={editDraft}
                onChange={(e) => setEditDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (submit(e)) saveEdit(c.id);
                  if (e.key === 'Escape') cancelEdit();
                }}
                autoFocus
                rows={2}
                style={textareaStyle(reply, true)}
              />
              <SmallActions primary="Save" onPrimary={() => saveEdit(c.id)} onCancel={cancelEdit} />
            </div>
          )}
          {canEdit ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, marginLeft: -8 }}>
              <Button variant="ghost" size={reply ? 24 : 26} px={8} fontSize={12} radius={5} onClick={() => { setEditId(c.id); setEditDraft(c.text); setReplyTo(null); }}>
                Edit
              </Button>
              {onDelete ? (
                <Button variant="ghost" size={reply ? 24 : 26} px={8} fontSize={12} radius={5} onClick={() => onDelete(c.id)}>
                  Delete
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  const composerBox = canPost ? (
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <Avatar name={me.name} size={28} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (submit(e)) post();
              }}
              rows={2}
              placeholder={placeholder}
              style={textareaStyle()}
            />
            {draft.trim() ? (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                <span style={{ fontSize: 12, color: 'var(--tr-hint)' }}>⌘↵ to post</span>
                <Button variant="outlined" size={30} px={12} fontSize={13} onClick={post}>
                  Post
                </Button>
              </div>
            ) : null}
          </div>
        </div>
      ) : null;
  const signinBox = !canPost && onSignin ? (
        <div style={{ padding: '12px 14px', border: '1px solid var(--tr-line)', borderRadius: 8, background: 'var(--tr-soft)', fontSize: 13, color: 'var(--tr-muted)' }}>
          <TextLink onClick={onSignin} fontSize={13} underline hover="none">
            Sign in
          </TextLink>{' '}
          to join the discussion.
        </div>
      ) : null;
  const emptyBox = comments.length === 0 ? <div style={{ fontSize: 13, color: 'var(--tr-hint)' }}>{emptyText}</div> : null;
  const list = comments.map((c) => {
        const replying = replyTo === c.id;
        const replies = c.replies ?? [];
        return (
          <div key={c.id} style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {row(c, false)}
            {replies.length ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginLeft: 38, paddingLeft: 12, borderLeft: '2px solid var(--tr-line)' }}>
                {replies.map((r) => row(r, true))}
              </div>
            ) : null}
            {canPost && !replying ? (
              <div style={{ display: 'flex', marginLeft: 38, paddingLeft: 4, marginTop: -4 }}>
                <Button variant="ghost" size={26} px={8} fontSize={12} radius={5} onClick={() => { setReplyTo(c.id); setReplyDraft(''); setEditId(null); }}>
                  Reply
                </Button>
              </div>
            ) : null}
            {replying ? (
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginLeft: 38, paddingLeft: 12, borderLeft: '2px solid var(--tr-line)' }}>
                <Avatar name={me.name} size={22} style={{ fontSize: 8 }} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, minWidth: 0 }}>
                  <textarea
                    value={replyDraft}
                    onChange={(e) => setReplyDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (submit(e)) sendReply(c.id);
                      if (e.key === 'Escape') { setReplyTo(null); setReplyDraft(''); }
                    }}
                    autoFocus
                    rows={2}
                    placeholder={`Reply to ${c.name.split(' ')[0]}`}
                    style={textareaStyle(true, true)}
                  />
                  <SmallActions primary="Reply" onPrimary={() => sendReply(c.id)} onCancel={() => { setReplyTo(null); setReplyDraft(''); }} />
                </div>
              </div>
            ) : null}
          </div>
        );
      });

  const font = "'Instrument Sans', system-ui, sans-serif";
  if (composer === 'bottom') {
    const footer = composerBox ?? signinBox;
    return (
      <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: font, color: 'var(--tr-ink)' }}>
        <div ref={listRef} data-testid="comment-list" style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 18 }}>
          {emptyBox}
          {list}
        </div>
        {footer ? <div style={{ flex: 'none', borderTop: '1px solid var(--tr-line)', padding: '10px 16px 12px' }}>{footer}</div> : null}
      </div>
    );
  }
  return (
    <div onClick={(e) => e.stopPropagation()} style={{ display: 'flex', flexDirection: 'column', gap: 18, fontFamily: font, color: 'var(--tr-ink)' }}>
      {composerBox}
      {signinBox}
      {emptyBox}
      {list}
    </div>
  );
}
